import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import useSeo from "@/hooks/useSeo";
import { CATEGORIES, CASES } from "@/mocks/archive";

const prettyCat = (slug: string) => CATEGORIES.find((c) => c.slug === slug)?.name || "档案";

export default function Archive() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const tag = params.get("tag") || "";
  const q = params.get("q") || "";
  const [kw, setKw] = useState(q);

  const current = cat === "all" ? null : CATEGORIES.find((c) => c.slug === cat) || null;

  const scoped = useMemo(
    () => (current ? CASES.filter((c) => c.category === current.slug) : CASES),
    [current]
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    scoped.forEach((c) => c.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [scoped]);

  const list = useMemo(() => {
    return scoped.filter((c) => {
      const hitTag = !tag || c.tags.includes(tag);
      const hitQ =
        !q || c.title.includes(q) || c.excerpt.includes(q) || c.tags.some((t) => t.includes(q));
      return hitTag && hitQ;
    });
  }, [scoped, tag, q]);

  const applyCat = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    next.delete("tag");
    setParams(next, { replace: false });
  };

  const applyTag = (t: string) => {
    const next = new URLSearchParams(params);
    if (tag === t) next.delete("tag");
    else next.set("tag", t);
    setParams(next, { replace: false });
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (kw.trim()) next.set("q", kw.trim());
    else next.delete("q");
    setParams(next, { replace: false });
  };

  const seoTitle = current
    ? `${current.name}黑料 - 明星黑料分类档案盘点`
    : "明星黑料分类档案库 - 按标签检索人品争议感情纠纷耍大牌翻车";
  const seoDesc = current
    ? `${current.name}黑料档案合集：${current.sample.slice(0, 4).join("、")}等标签，系统整理${current.name}相关艺人争议全过程。`
    : "全部分类档案：人品争议、感情纠纷、耍大牌、违约失信、言论翻车、行为失当六大主题，支持标签筛选与关键词检索。";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: seoTitle,
      url: "https://gjkmv.cn/archive",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: list.length,
        itemListElement: list.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.title,
          url: `https://gjkmv.cn/case/${c.slug}`,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://gjkmv.cn/" },
        { "@type": "ListItem", position: 2, name: "分类档案库", item: "https://gjkmv.cn/archive" },
      ],
    },
  ];

  useSeo({ title: seoTitle, description: seoDesc, keywords: "明星黑料,明星黑料分类,明星黑料档案,娱乐圈塌房盘点", path: "/archive", jsonLd });

  return (
    <PageShell>
      {/* 面包屑 + 标题 */}
      <section className="bg-background-50 pt-28 md:pt-32 pb-6">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 whitespace-nowrap">分类档案库</span>
            {current && (
              <>
                <i className="ri-arrow-right-s-line" />
                <span className="text-primary-700 font-medium whitespace-nowrap">{current.name}黑料</span>
              </>
            )}
          </nav>
          <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-primary-600 uppercase">
                <span className="h-px w-8 bg-primary-400" /> Archive
              </p>
              <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                {current ? (
                  <>明星{current.name}黑料<span className="text-primary-600">档案合集</span></>
                ) : (
                  <>明星黑料<span className="text-primary-600">分类档案库</span></>
                )}
              </h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600">
                {current ? current.desc : "六大分类按标签快速检索，全面梳理艺人塌房全过程。当前共收录档案条目 "}
                {!current && <span className="text-primary-700 font-semibold">{CASES.length} 条</span>}
              </p>
            </div>
            <form onSubmit={onSearch} className="w-full max-w-sm">
              <div className="flex items-center gap-2 rounded-full border border-background-300/80 bg-background-100 px-3 py-1.5 focus-within:border-primary-400">
                <i className="ri-search-line text-foreground-400" />
                <input
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  placeholder="搜索档案关键词…"
                  className="w-full bg-transparent py-1.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
                <button type="submit" className="grid place-items-center w-8 h-8 rounded-full bg-primary-600 text-background-50 hover:bg-primary-700 transition-colors" aria-label="搜索">
                  <i className="ri-arrow-right-line text-sm" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* 筛选区 */}
      <section className="border-y border-background-300/70 py-5 sticky top-16 lg:top-[98px] z-30 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 space-y-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1" role="tablist">
            <button
              type="button"
              role="tab"
              onClick={() => applyCat("all")}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                cat === "all" ? "bg-primary-600 text-background-50 font-medium" : "bg-background-50 text-foreground-700 hover:bg-background-200/70 border border-background-300/70"
              }`}
            >
              全部档案 <span className={cat === "all" ? "text-background-100" : "text-foreground-400"}>{CASES.length}</span>
            </button>
            {CATEGORIES.map((c) => {
              const n = CASES.filter((x) => x.category === c.slug).length;
              const active = cat === c.slug;
              return (
                <button
                  key={c.slug}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => applyCat(c.slug)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                    active ? "bg-primary-600 text-background-50 font-medium" : "bg-background-50 text-foreground-700 hover:bg-background-200/70 border border-background-300/70"
                  }`}
                >
                  <i className={c.icon} />
                  {c.name}黑料 <span className={active ? "text-background-100" : "text-foreground-400"}>{n}</span>
                </button>
              );
            })}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-400 whitespace-nowrap">细分标签：</span>
              {tags.map((t) => {
                const active = tag === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => applyTag(t)}
                    className={`shrink-0 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs whitespace-nowrap transition-colors ${
                      active ? "bg-accent-500 text-primary-950 font-medium" : "bg-background-50 border border-background-300/70 text-foreground-600 hover:border-accent-400 hover:text-accent-700"
                    }`}
                  >
                    {t}
                    {active && <i className="ri-close-line" />}
                  </button>
                );
              })}
            </div>
          )}

          {(tag || q) && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
              当前筛选：
              {cat !== "all" && <span className="rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-800">{prettyCat(cat)}黑料</span>}
              {tag && <button onClick={() => applyTag(tag)} className="rounded bg-accent-100 px-2 py-0.5 text-xs text-accent-800 hover:bg-accent-200">标签「{tag}」<i className="ri-close-line" /></button>}
              {q && <button onClick={() => { const n = new URLSearchParams(params); n.delete("q"); setParams(n); setKw(""); }} className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900 hover:bg-secondary-200">关键词「{q}」<i className="ri-close-line" /></button>}
              <span className="text-foreground-400">共 {list.length} 条</span>
            </p>
          )}
        </div>
      </section>

      {/* 结果列表 */}
      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-20 text-center px-6">
              <span className="grid place-items-center w-14 h-14 rounded-full bg-secondary-100 text-secondary-700 text-2xl"><i className="ri-file-search-line" /></span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有匹配的档案</h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">换个分类、清空标签或关键词再试试，六大分类都有丰富条目等你检索。</p>
              <button onClick={() => { setParams(new URLSearchParams()); setKw(""); }} className="mt-5 rounded-full bg-primary-600 text-background-50 px-5 py-2.5 text-sm font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap">
                重置全部筛选
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((item) => {
                const c = CATEGORIES.find((x) => x.slug === item.category)!;
                return (
                  <article key={item.slug} className="group flex flex-col rounded-xl border border-background-300/70 bg-background-100 p-5 transition-all hover:-translate-y-1 hover:border-primary-300">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium ${c.accent === "accent" ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-800"}`}>
                        <i className={c.icon} /> {c.name}黑料
                      </span>
                      <span className="text-[11px] text-foreground-400">{item.published}</span>
                    </div>
                    <h2 className="mt-3 font-heading text-base md:text-lg font-bold leading-7 text-foreground-950 group-hover:text-primary-700 transition-colors">
                      <Link to={`/case/${item.slug}`} className="line-clamp-2">{item.title}</Link>
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-2">{item.excerpt}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {item.tags.slice(0, 3).map((t) => (
                        <span key={t} className="rounded bg-background-50 border border-background-300/60 px-1.5 py-0.5 text-[11px] text-foreground-500">{t}</span>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-background-300/60 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 text-xs text-accent-600"><i className="ri-fire-fill" />热度 {item.heat}/5</span>
                      <Link to={`/case/${item.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap">
                        查看档案 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}